import React, { useRef, useEffect, useState } from "react";
import { usePopper } from "react-popper";
import { useNavigate } from "react-router-dom";
import cn from "classnames";
import style from "./NotificationUserPopup.module.scss";
import CloseIconNew from "../../shared/icons/CloseIconNew";
import HandshakeIcon from "../../shared/icons/HandshakeIcon";
import UsersIcon from "../../shared/icons/UsersIcon";
import { MessageSquare, Building2 } from "lucide-react";
import { Notification } from "../../../services";
import { NOTIFICATION_TYPES } from "../../../mock";
import { useTranslation } from "react-i18next";
import Loading from "../../shared/Loading/Loading";

const NotificationUserPopup = ({ isOpen, onClose, referenceElement, onNotificationsUpdated, onOpenChat }) => {
  const { t } = useTranslation();
  const popupRef = useRef(null);
  const containerRef = useRef(null);
  const [popperElement, setPopperElement] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement: "bottom-end",
    modifiers: [
      {
        name: "preventOverflow",
        options: { boundary: "viewport" },
      },
      {
        name: "offset",
        options: { offset: [0, 8] },
      },
    ],
  });

  useEffect(() => {
    const fetchNotifications = async () => {
      if (isOpen) {
        setIsLoading(true);
        try {
          const response = await Notification.getNotificationsList({
            page: 1,
            limit: 10,
          });
          setNotifications(response.data.rows || []);
        } catch (error) {
          console.error("Ошибка при загрузке уведомлений:", error);
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchNotifications();
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(event.target) &&
        referenceElement &&
        !referenceElement.contains(event.target)
      ) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose, referenceElement]);

  const handleMarkAllRead = async () => {
    const unreadNotifications = notifications.filter((n) => !n.isRead);

    if (unreadNotifications.length === 0) return;

    try {
      await Promise.all(unreadNotifications.map((n) => Notification.markUserNotificationAsRead(n.id)));

      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));

      if (onNotificationsUpdated) {
        onNotificationsUpdated();
      }
    } catch (err) {
      console.error("Ошибка при отметке уведомлений как прочитанных", err);
    }
  };

  const handleNotificationClick = async (notification) => {
    if (!notification.isRead) {
      try {
        await Notification.markUserNotificationAsRead(notification.id);
        setNotifications((prev) => prev.map((n) => (n.id === notification.id ? { ...n, isRead: true } : n)));
        if (onNotificationsUpdated) {
          onNotificationsUpdated();
        }
      } catch (err) {
        console.error("Ошибка при отметке уведомления как прочитанного", err);
      }
    }

    if (notification.type === "MESSAGES" && notification.orderId && onOpenChat) {
      const serviceType = notification.orderType === "AIRORDER" ? "aviation" : "order";
      onOpenChat(notification.orderId, serviceType);
      onClose();
      return;
    }

    const notificationTypeConfig = Object.values(NOTIFICATION_TYPES(t)).find(
      (type) => type.key === notification.type,
    );

    onClose();
    if (notificationTypeConfig?.link) {
      navigate(notificationTypeConfig.link);
    }
  };

  const getNotificationIcon = (type) => {
    const iconStyle = { width: "16px", height: "16px" };

    switch (type) {
      case NOTIFICATION_TYPES(t).PARTNERSHIP.key:
        return (
          <div className={cn(style.iconWrapper, style.iconPartnership)}>
            <div style={iconStyle}>
              <HandshakeIcon />
            </div>
          </div>
        );
      case NOTIFICATION_TYPES(t).MESSAGES.key:
        return (
          <div className={cn(style.iconWrapper, style.iconMessages)}>
            <MessageSquare size={16} />
          </div>
        );
      case NOTIFICATION_TYPES(t).COMPANY.key:
        return (
          <div className={cn(style.iconWrapper, style.iconCompany)}>
            <Building2 size={16} />
          </div>
        );
      case NOTIFICATION_TYPES(t).EMPLOYEES.key:
        return (
          <div className={cn(style.iconWrapper, style.iconEmployees)}>
            <div style={iconStyle}>
              <UsersIcon />
            </div>
          </div>
        );
      default:
        return (
          <div className={cn(style.iconWrapper, style.iconDefault)}>
            <MessageSquare size={16} />
          </div>
        );
    }
  };

  const formatTime = (date) => {
    if (!date) return "";

    const notificationDate = new Date(date);
    const now = new Date();
    const diffInHours = (now - notificationDate) / (1000 * 60 * 60);

    if (notificationDate.getFullYear() !== now.getFullYear()) {
      const day = String(notificationDate.getDate()).padStart(2, "0");
      const month = String(notificationDate.getMonth() + 1).padStart(2, "0");
      const year = notificationDate.getFullYear();

      return `${day}.${month}.${year}`;
    }

    if (diffInHours > 24) {
      const day = notificationDate.getDate();
      const monthNames = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
      const month = monthNames[notificationDate.getMonth()];

      return `${day} ${t(`common.datetime.short_months_lowercase.${month}`)}.`;
    }

    return notificationDate.toLocaleTimeString("ru-RU", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleViewAll = () => {
    onClose();
    navigate("/notification");
  };

  if (!isOpen) return null;

  return (
    <div ref={setPopperElement} style={styles.popper} {...attributes.popper} className={cn(style.popup)}>
      <div ref={popupRef} className={style.popupInner}>
        {/* Header */}
        <div className={style.header}>
          <h3 className={style.headerTitle}>{t("notificationPage.header.title")}</h3>
          <div className={style.headerActions}>
            <button
              className={style.markAllReadBtn}
              onClick={handleMarkAllRead}
              disabled={notifications.filter((n) => !n.isRead).length === 0}
            >
              {t("notificationPage.buttons.markAllRead")}
            </button>
            <button className={style.closeBtn} onClick={onClose}>
              <CloseIconNew />
            </button>
          </div>
        </div>

        {/* Notifications List */}
        <div ref={containerRef} className={style.notificationsList}>
          {isLoading ? (
            <div className={style.empty}>
              <Loading inBlock />
            </div>
          ) : notifications.length === 0 ? (
            <div className={style.empty}>{t("notificationPage.noNotifications")}</div>
          ) : (
            notifications.map((notification) => (
              <div
                key={notification.id}
                className={cn(style.notificationItem, {
                  [style.unread]: !notification.isRead,
                })}
                onClick={() => handleNotificationClick(notification)}
                style={{ cursor: "pointer" }}
              >
                {/* Icon */}
                {getNotificationIcon(notification.type)}

                {/* Content */}
                <div className={style.notificationContent}>
                  <div className={style.notificationHeader}>
                    <h4 className={style.notificationTitle}>{notification.title}</h4>
                    <span className={style.notificationTime}>{formatTime(notification.createdAt)}</span>
                    {!notification.isRead && <div className={style.unreadDot} />}
                  </div>
                  <p className={style.notificationText}>{notification.text}</p>
                </div>

                {/* Unread indicator */}
                {/* {!notification.isRead && <div className={style.unreadDot} />} */}
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className={style.footer}>
          <button className={style.viewAllBtn} onClick={handleViewAll}>
            {t("notificationPage.viewAll")} →
          </button>
        </div>
      </div>
    </div>
  );
};

export default NotificationUserPopup;
